import type { TapPageIndexing } from '@inktap/contracts/tap-page-indexing';

type TapPageSharing = {
  state: 'ready' | 'inactive' | 'unpublished' | 'unavailable';
  kind: 'public' | 'private' | null;
  href: string | null;
};

type DashboardSummary = {
  user: {
    email: string;
  };
  plan: {
    plan: string | null;
    status: 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | null;
    active: boolean;
    entitlements: {
      canSyncToCloud: boolean;
      canManagePublicAlias: boolean;
      canControlIndexability: boolean;
      canUseBookingEmbeds: boolean;
      canUseAnalytics: boolean;
      canUseProtectedResources: boolean;
      canManagePhysicalCards: boolean;
      canDownloadProof: boolean;
    };
    limits: {
      actionLimit: number | null;
      uploadStorageMb: number | null;
      analyticsLevel: 'none' | 'basic' | 'advanced' | 'security';
      supportLevel: 'standard' | 'priority';
    };
  };
  tapPage: {
    sharing: TapPageSharing;
    indexing?: TapPageIndexing;
    slugId: string;
    slug: string;
    active: boolean;
    publicSlug: string | null;
    indexable: boolean;
    status: 'not_started' | 'draft' | 'published';
    draftUpdatedAt: string | null;
    publishedAt: string | null;
    updatedAt: string | null;
    preview: {
      theme: 'auto' | 'light' | 'dark';
      profile: {
        name: string;
        title: string;
        subtitle?: string;
        bio?: string;
        photoUrl?: string;
      };
      actions: Array<{
        id: string;
        type: string;
        label: string | null;
        provider: string | null;
      }>;
    } | null;
    links: {
      tap: string | null;
      publicAlias: string | null;
      selfView: string | null;
    };
  } | null;
  card: {
    status: 'not_started' | 'design_draft' | 'checkout_pending' | 'processing' | 'shipped' | 'canceled';
    latestDesign: {
      id: string;
      styleId: string;
      previewImageUrl: string | null;
      quantity: number | null;
      updatedAt: string;
    } | null;
    latestOrder: {
      id: string;
      status: string;
      material: string;
      styleId: string | null;
      previewImageUrl: string | null;
      quantity: number;
      createdAt: string;
      updatedAt: string;
    } | null;
  };
  recentActivity: Array<{
    id: string;
    type: string;
    label: string;
    occurredAt: string | null;
  }>;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return 'Not yet';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Not yet';
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(parsed);
};

const formatPlan = (value: string | null | undefined) => {
  if (!value) {
    return 'Free';
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const formatStatus = (value: string) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

async function copyToClipboard(value: string) {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement('input');
  input.value = value;
  document.body.appendChild(input);
  input.select();
  try {
    if (!document.execCommand('copy')) throw new Error('Clipboard copy failed');
  } finally {
    document.body.removeChild(input);
  }
}

export { copyToClipboard, formatDate, formatPlan, formatStatus };
export type { DashboardSummary, TapPageSharing };
